import toastr from 'react-hot-toast';
import { useQuery } from 'react-query';
import { fetchWrapper } from 'utils/fetchWrapper';
import { CACHE_AND_STALE_TIME } from 'constants/index';
import { ID_MAP_KEYS, SPARK_RESOURCE_TYPES } from 'constants/common';
import {
  generateFilter,
  generateIncludeQuery,
  generateURL,
} from 'utilities/hooks/urlGeneration';
import { useCurrentUser } from 'hooks/common/useCurrentUser';
import type { Memory, MemoryJoinedFavouriteResponse } from 'types/Memory';
import React from 'react';
import {
  filterIncludedItem,
  generateMap,
} from 'utilities/hooks/responseHandlers';
import { FavouriteList } from 'types/FavouriteList';
import { JoinedMemory } from 'types/Memory';
import { createSingleJoinedMemory } from 'helpers/spark';
type GroupedFavourites = {
  memory: JoinedMemory[];
  favourite: FavouriteList;
};
const getMemoryFavourites = (userID: string) => {
  const queryArray = [
    generateFilter('userID', userID),
    generateIncludeQuery(['memory', 'favouriteList']),
  ];
  const url = generateURL(SPARK_RESOURCE_TYPES.MEMORY_FAVOURITES, queryArray);
  return fetchWrapper<MemoryJoinedFavouriteResponse>({
    url: url,
    method: 'GET',
  });
};

export function useMemoryFavourites() {
  const { currentUserID } = useCurrentUser();
  const response = useQuery(
    'memoryFavourites',
    () => getMemoryFavourites(currentUserID || ''),
    {
      onError: (error) => {
        if (error instanceof Error) {
          toastr.error(
            `failed to Fetch JoinedMemory Favourites: ${error.message}`
          );
        }
      },
      ...CACHE_AND_STALE_TIME,
    }
  );
  const { data: memoryFavourite, included } = response.data || {};
  const memories = filterIncludedItem<Memory>(
    included,
    SPARK_RESOURCE_TYPES.MEMORIES
  );
  const joinedMemories = memories
    ?.map((memory) => {
      return createSingleJoinedMemory({
        data: memory,
        included: memoryFavourite || [],
      });
    })
    .filter((item): item is JoinedMemory => !!item);

  const favouriteList = filterIncludedItem<FavouriteList>(
    included,
    SPARK_RESOURCE_TYPES.FAVOURITE_LIST
  );

  const memoriesIDMap = generateMap<JoinedMemory>(
    joinedMemories,
    ID_MAP_KEYS.ID
  );
  const favouriteListIDMap = generateMap<FavouriteList>(
    favouriteList,
    ID_MAP_KEYS.ID
  );

  const memoriesGroupedByFavouriteID = React.useMemo<
    { [id: string]: GroupedFavourites } | undefined
  >(() => {
    return memoryFavourite?.reduce((acc, curr) => {
      const favID = curr.attributes.favouriteListID;
      const memory = memoriesIDMap?.[curr.attributes.memoryID];
      const favourite = favouriteListIDMap?.[favID];

      if (memory && favourite) {
        if (!acc[favID]) {
          acc[favID] = { favourite, memory: [] };
        }
        acc[favID].memory.push(memory);
      }
      return acc;
    }, {} as { [id: string]: GroupedFavourites });
  }, [favouriteListIDMap, memoriesIDMap, memoryFavourite]);

  return {
    ...response,
    joinedMemories,
    memoriesGroupedByFavouriteID,
    memoryFavourite,
  };
}
